import * as React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./select";
import { Button } from "./button";
import { Label, Text } from "./typography";
import { API_BASE_URL } from '../../lib/api';

const CATEGORIES = [
  { value: "Dividend", label: "Dividend" },
  { value: "Interest", label: "Interest" },
  { value: "Rental", label: "Rental Income" },
  { value: "Capital Gains", label: "Capital Gains" },
  { value: "Salary", label: "Salary" },
  { value: "Bonus", label: "Bonus" },
  { value: "Freelance", label: "Freelance" },
  { value: "Other", label: "Other" },
];

const FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "one-time", label: "One-Time" },
];

const CURRENCIES = [
  { value: "EUR", label: "EUR - Euro" },
  { value: "USD", label: "USD - US Dollar" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "SEK", label: "SEK - Swedish Krona" },
];

/**
 * EditIncomeDialog - Dialog for editing an existing income entry
 */
export function EditIncomeDialog({
  open,
  onOpenChange,
  onSuccess,
  income,
}) {
  const [formData, setFormData] = React.useState({
    name: "",
    category: "Dividend",
    amount: "",
    currency: "EUR",
    frequency: "monthly",
    date: "",
    notes: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Populate form when income changes
  React.useEffect(() => {
    if (open && income) {
      setFormData({
        name: income.name || "",
        category: income.category || "Dividend",
        amount: income.amount?.toString() || "",
        currency: income.currency || "EUR",
        frequency: income.frequency || "monthly",
        date: income.date || "",
        notes: income.notes || "",
      });
      setError(null);
    }
  }, [open, income]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      return "Name is required";
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      return "Amount must be positive";
    }
    if (!formData.date) {
      return "Date is required";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        frequency: formData.frequency,
        date: formData.date,
        notes: formData.notes || "",
      };

      await axios.put(
        `${API_BASE_URL}/income/${income.incomeId}`,
        payload,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to update income:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!income) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle>Edit Income</DialogTitle>
          <DialogDescription>
            Update the details of this income entry
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Income Details
            </legend>

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Name *</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., AAPL Dividend, Rental Income, Freelance Project"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => handleSelectChange("category", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Frequency *</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(value) => handleSelectChange("frequency", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Amount *</Label>
                <Input
                  name="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="e.g., 100.00"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Currency *</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleSelectChange("currency", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Date *</Label>
              <Input
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Notes</Label>
              <Input
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Optional notes..."
              />
            </div>
          </fieldset>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditIncomeDialog;
